/**
 * Feature flags.
 *
 * A flag being `false` must remove the affordance entirely, not disable a
 * visible button. Nothing in the UI may advertise a capability that is not
 * actually wired up - that is the rule this module exists to enforce.
 */

import { env } from './env.js';

export const features = {
  /** Master switch for public WebGL experiences. */
  threeDimensional: env.enable3d,

  /**
   * Small decorative 3D accents inside the authenticated portals. Independent
   * of `threeDimensional` so the marketing scenes and the portal accents can be
   * tuned separately.
   */
  portalThreeDimensionalAccents: env.enable3d && env.enablePortal3dAccents,

  /**
   * Payment checkout. While false, packages display as enquiry-only and the
   * admin portal records payments as manual references. No checkout button is
   * rendered anywhere.
   */
  payments: env.enablePayments,

  /**
   * Google / Microsoft calendar connection. While false the UI makes no claim
   * about external sync; ICS export remains available and is genuinely working.
   */
  externalCalendarSync: env.enableExternalCalendar,

  /** Multi-factor enrolment UI. Depends on platform support. */
  multiFactorAuth: env.enableMfa,

  /** Waitlist affordances on fully booked slots. */
  waitlist: env.enableWaitlist,

  /**
   * Installable Progressive Web App prompt on mobile. Manifest + service worker
   * ship with the SPA; Vite PWA plugin hardening can replace the SW later.
   */
  pwa: env.enablePwa,

  /**
   * Web Push subscription UI. Affordance is hidden when the flag is off; the
   * settings card still explains when a VAPID key is not yet configured.
   */
  pushNotifications: env.enablePushNotifications,

  /** Privacy-conscious analytics. Off unless an endpoint is configured. */
  analytics: env.analyticsEnabled && env.analyticsEndpoint !== null,

  /** Development-only 3D performance overlay. */
  performanceOverlay: env.debugPerformanceOverlay && env.isDevelopment,
} as const;

export type FeatureName = keyof typeof features;

export function isEnabled(name: FeatureName): boolean {
  return features[name];
}
